import { Pass, Resolution, Effect, BlendFunction } from 'postprocessing';
import {
    Scene,
    Texture,
    TextureDataType,
    Uniform,
    Vector2,
    WebGLArrayRenderTarget,
    WebGLRenderer,
    WebGLRenderTarget,
    PerspectiveCamera,
} from 'three';
import { KawaseLightStreakProps } from '../../../components/three/KawaseLightStreak';
import KawaseLightStreakHighPass_FRAG from '../shaders/KawaseLightStreakHighPass_FRAG.glsl'; // KawaseLightStreakFinalOutput_FRAG
import KawaseLightStreakFinalOutput_FRAG from '../shaders/KawaseLightStreakFinalOutput_FRAG.glsl';
import { KawaseLightStreakBlurPass } from './KawaseLightStreakBlurPass';
import { kawasePassRenderTargetOptions } from '../lightStreakPPEffectPasses';

export class KawaseHighPassEffect extends Effect {
    threshold: KawasePassProps['threshold'];
    opacity: KawasePassProps['opacity'];

    constructor(threshold: KawasePassProps['threshold'], opacity: KawasePassProps['opacity']) {
        super('KawaseHighPassEffect', KawaseLightStreakHighPass_FRAG, {
            uniforms: new Map([
                ['threshold', new Uniform(threshold)],
                ['opacity', new Uniform(opacity)],
            ]),
        });

        this.threshold = threshold;
        this.opacity = opacity;
    }

    update(_renderer: WebGLRenderer, _inputBuffer: WebGLRenderTarget<Texture>, _deltaTime?: number | undefined): void {
        this.uniforms.get('threshold')!.value = this.threshold;
        this.uniforms.get('opacity')!.value = this.opacity;
    }
}

export class KawaseStreaksEffect extends Effect {
    k_lightStreakBlurPass: KawaseLightStreakBlurPass;
    streaksEffectRTarget: WebGLRenderTarget;
    resolution: Resolution;
    enabled: boolean;

    constructor({
        scene,
        camera,
        blendFunction = BlendFunction.ADD, // should be ADD, set SRC to debug incoming
        width,
        height,
        resolutionScale,
        attenuation,
        blurPasses,
        pixelSize,
        directions,
        enabled,
    }: {
        scene: Scene;
        camera: PerspectiveCamera;
        blendFunction?: BlendFunction;
        width: number;
        height: number;
        resolutionScale: number;
        attenuation: KawasePassProps['attenuation'];
        blurPasses: KawasePassProps['blurPasses'];
        pixelSize: KawasePassProps['pixelSize'];
        directions: KawasePassProps['directions'];
        enabled: boolean;
    }) {
        super('KawaseStreaksEffect', KawaseLightStreakFinalOutput_FRAG, {
            blendFunction,
            uniforms: new Map([['streaks', new Uniform(null)]]),
        });

        this.enabled = enabled;

        this.streaksEffectRTarget = new WebGLRenderTarget(1, 1, kawasePassRenderTargetOptions);
        this.streaksEffectRTarget.texture.name = 'KawaseStreaksEffect.Target';

        this.uniforms.get('streaks')!.value = this.streaksEffectRTarget.texture;

        this.k_lightStreakBlurPass = new KawaseLightStreakBlurPass(
            scene,
            camera,
            attenuation,
            blurPasses,
            pixelSize,
            directions,
            width,
            height,
            resolutionScale,
            this.enabled,
        );

        this.resolution = new Resolution(this, width, height, resolutionScale);
        this.resolution.addEventListener('change', (_e) => this.setSize(width, height));
    }

    setSize(width: number, height: number) {
        this.resolution.setBaseSize(width, height);
        this.streaksEffectRTarget.setSize(this.resolution.width / 2, this.resolution.height / 2);
        this.k_lightStreakBlurPass.resolution.copy(this.resolution);
    }

    initialize(renderer: WebGLRenderer, alpha: boolean, frameBufferType: TextureDataType) {
        this.k_lightStreakBlurPass.initialize(renderer, alpha, frameBufferType);
        if (frameBufferType !== undefined) {
            this.streaksEffectRTarget.texture.type = frameBufferType;
        }
    }

    update(renderer: WebGLRenderer, inputBuffer: WebGLRenderTarget<Texture>, _deltaTime?: number | undefined) {
        this.k_lightStreakBlurPass.render(renderer, inputBuffer, this.streaksEffectRTarget);
    }
}

export type KawasePassProps = KawaseLightStreakProps & {
    resolution: Resolution;
    pixelSize: Vector2;
    depthBuffer: WebGLRenderTarget;
    directions: Vector2[];
    streaksArrayRTarget: WebGLArrayRenderTarget;
    renderToScreen: Pass['renderToScreen'];
    needsSwap: Pass['needsSwap'];
};
